import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";

const themes = [
  {
    number: "01",
    title: "Local evidence matters",
    text: "Precision health needs evidence that reflects African populations, environments, disease patterns and healthcare realities. Global evidence remains valuable, but it cannot always answer questions that are specific to African contexts.",
  },
  {
    number: "02",
    title: "Data needs infrastructure",
    text: "Better evidence is only useful when health data can be collected, structured, governed and connected. Standards, interoperability and stronger data infrastructure are foundational to turning information into intelligence.",
  },
  {
    number: "03",
    title: "Build around real patient pathways",
    text: "Patients already move through pharmacies, primary healthcare centres, laboratories, hospitals and other points of care. A stronger health-data ecosystem should work with these existing pathways rather than assume an entirely new system.",
  },
  {
    number: "04",
    title: "Translation is the real challenge",
    text: "The opportunity is not simply to generate more research or build more technology. It is to create the pathways through which evidence becomes usable tools, clinical practice and measurable health outcomes.",
  },
];

const highlights = [
  "African populations need stronger locally relevant evidence to support precision-health research and practice.",
  "Genomic, clinical and environmental information become more valuable when they can be interpreted together.",
  "Fragmented and inconsistently structured data limits the ability to generate useful intelligence from existing healthcare activity.",
  "Pharmacies and primary healthcare can play important roles in a more connected health-data and care ecosystem.",
  "Technology can help transform unstructured information into more consistent and usable data.",
  "Standards and interoperability are essential if health information is to move meaningfully across institutions and care pathways.",
  "Precision health should strengthen existing health-system architecture rather than become another parallel layer.",
  "The long-term opportunity is to connect evidence, data, diagnostics, clinical care, policy, financing and implementation.",
];

const progression = [
  "Local evidence",
  "Structured data",
  "Actionable intelligence",
  "Better decisions",
  "Better care",
  "Measurable outcomes",
];

function SeminarDataToIntelligencePage() {
  return (
    <Box className="serena-page" bg="#f9faf8">
      <Box bg="#f3f0e8">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28, lg: 32 }}
        >
          <Stack spacing={7}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#087f73"
            >
              Serena Space Institute
            </Text>

            <Text fontSize="sm" color="#4c5955">
              6 October 2026
            </Text>

            <Heading
              as="h1"
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
              lineHeight="1.05"
              letterSpacing="-0.045em"
              fontWeight="600"
              color="#17211f"
              maxW="950px"
            >
              From African Health Data to Actionable Intelligence
            </Heading>

            <Text
              fontSize={{ base: "lg", md: "xl" }}
              lineHeight="1.75"
              color="#4c5955"
              maxW="800px"
            >
              A synthesis of the Serena Space Institute conversation on how
              African health data and evidence can become better decisions,
              better care and better outcomes.
            </Text>
          </Stack>
        </Container>
      </Box>

      <Box>
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 10, md: 20 }}
          >
            <Box>
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="#087f73"
                mb={5}
              >
                The question
              </Text>
              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
              >
                What has to happen for African health data and evidence to
                become better decisions, better care and better outcomes?
              </Heading>
            </Box>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="#4c5955"
            >
              The conversation brought together perspectives from health
              research, clinical practice, health systems, technology and
              policy, focusing on what has to happen between generating
              evidence and creating meaningful health impact.
            </Text>
          </SimpleGrid>
        </Container>
      </Box>

      <Box bg="#17211f" color="white">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={6} mb={{ base: 12, md: 16 }}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#7bcfc3"
            >
              Four themes
            </Text>
            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="850px"
            >
              What it takes to turn evidence into impact.
            </Heading>
          </Stack>

          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing="1px"
            bg="whiteAlpha.200"
          >
            {themes.map((theme) => (
              <Box
                key={theme.number}
                bg="#17211f"
                p={{ base: 7, md: 9 }}
                minH={{ md: "290px" }}
              >
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  letterSpacing="0.12em"
                  color="whiteAlpha.500"
                  mb={8}
                >
                  {theme.number}
                </Text>
                <Heading
                  as="h3"
                  fontFamily="'Poppins', sans-serif"
                  fontSize="xl"
                  lineHeight="1.25"
                  fontWeight="600"
                  mb={4}
                >
                  {theme.title}
                </Heading>
                <Text fontSize="sm" lineHeight="1.75" color="whiteAlpha.700">
                  {theme.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <Box>
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={6} mb={{ base: 10, md: 14 }}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#087f73"
            >
              Conversation highlights
            </Text>
            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="850px"
            >
              Key points from the discussion.
            </Heading>
          </Stack>

          <Stack as="ol" spacing={0} listStyleType="none">
            {highlights.map((highlight, index) => (
              <Flex
                as="li"
                key={highlight}
                gap={{ base: 5, md: 8 }}
                py={{ base: 5, md: 6 }}
                borderTop="1px solid"
                borderColor="blackAlpha.150"
                align="flex-start"
              >
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  letterSpacing="0.12em"
                  color="#087f73"
                  flexShrink={0}
                >
                  {String(index + 1).padStart(2, "0")}
                </Text>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  lineHeight="1.75"
                  color="#4c5955"
                >
                  {highlight}
                </Text>
              </Flex>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box bg="#f3f0e8">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={6} mb={{ base: 10, md: 14 }}>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#087f73"
            >
              Longer-term direction
            </Text>
            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.1"
              letterSpacing="-0.04em"
              fontWeight="600"
              maxW="900px"
            >
              Precision health needs an information and care ecosystem.
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="#4c5955"
              maxW="950px"
            >
              The discussion pointed toward a longer-term vision in which
              African precision health is supported by stronger local evidence,
              structured health data, interoperable systems and a primary-care
              foundation capable of supporting prevention and earlier
              detection.
            </Text>
          </Stack>

          <Flex
            align="center"
            justify="center"
            gap={{ base: 3, md: 4 }}
            flexWrap="wrap"
            aria-label={progression.join(" to ")}
          >
            {progression.map((step, index) => (
              <React.Fragment key={step}>
                <Box
                  bg="white"
                  border="1px solid"
                  borderColor="blackAlpha.100"
                  borderRadius="xl"
                  px={{ base: 4, md: 5 }}
                  py={4}
                  textAlign="center"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color="#17211f"
                    whiteSpace="nowrap"
                  >
                    {step}
                  </Text>
                </Box>
                {index < progression.length - 1 && (
                  <Text
                    aria-hidden="true"
                    color="#087f73"
                    fontSize="xl"
                    fontWeight="600"
                  >
                    →
                  </Text>
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Container>
      </Box>

      <Box bg="#17211f" color="white">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 10, md: 16 }}
            alignItems="center"
          >
            <Box>
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.16em"
                textTransform="uppercase"
                color="#7bcfc3"
                mb={5}
              >
                What comes next
              </Text>
              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "5xl" }}
                lineHeight="1.1"
                letterSpacing="-0.04em"
                fontWeight="600"
              >
                From actionable intelligence to implementation.
              </Heading>
            </Box>

            <Stack spacing={6}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="whiteAlpha.700"
              >
                The next question follows naturally from this conversation:
                how do promising health technologies become functioning
                health-system capabilities?
              </Text>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="whiteAlpha.700"
              >
                The Institute is currently shaping its third seminar, tentatively
                targeted for the first week of November 2026, around the theme
                From Innovation to Implementation.
              </Text>
              <Flex gap={4} flexWrap="wrap" pt={2}>
                <Button
                  as="a"
                  href="/institute"
                  bg="#087f73"
                  color="white"
                  borderRadius="full"
                  px={6}
                  size="lg"
                  fontWeight="600"
                  _hover={{ bg: "#06695f", textDecoration: "none" }}
                >
                  Explore the Institute
                </Button>
                <Button
                  as="a"
                  href="/resources"
                  variant="outline"
                  borderColor="whiteAlpha.400"
                  color="white"
                  borderRadius="full"
                  px={6}
                  size="lg"
                  fontWeight="600"
                  _hover={{
                    bg: "whiteAlpha.100",
                    textDecoration: "none",
                  }}
                >
                  Explore resources
                </Button>
              </Flex>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
}

export default SeminarDataToIntelligencePage;
